import { useEffect } from 'react'
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  CreditCard,
  CalendarDays,
  CalendarClock,
  ShieldCheck,
  BarChart2,
  DollarSign,
  UserCog,
  ShoppingCart,
  UserPlus,
  FileSignature,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  Moon,
} from 'lucide-react'
import toast from 'react-hot-toast'
import useAuthStore from '../store/authStore.js'
import useUIStore, { applyTheme } from '../store/uiStore.js'

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/members', label: 'Members', icon: Users },
  { to: '/plans', label: 'Plans', icon: CreditCard },
  { to: '/schedule', label: 'Schedule', icon: CalendarDays },
  { to: '/appointments', label: 'Appointments', icon: CalendarClock },
  { to: '/gate', label: 'Gate', icon: ShieldCheck },
  { to: '/reports', label: 'Reports', icon: BarChart2 },
  { to: '/billing', label: 'Billing', icon: DollarSign },
  { to: '/pos', label: 'Point of Sale', icon: ShoppingCart },
  { to: '/leads', label: 'Leads', icon: UserPlus },
  { to: '/waivers', label: 'Waivers', icon: FileSignature },
  { to: '/staff', label: 'Staff', icon: UserCog },
]

function NavItem({ to, label, icon: Icon, collapsed }) {
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        [
          'group relative flex items-center rounded-lg text-sm font-medium',
          'transition-all duration-200 ease-snap border',
          collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5',
          isActive
            ? 'bg-primary/12 text-primary border-primary/30'
            : 'text-text-muted border-transparent hover:text-text-primary hover:bg-surface-3',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className={[
              'absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full bg-primary',
              'transition-all duration-200 ease-snap',
              isActive ? 'h-5 opacity-100' : 'h-0 opacity-0',
            ].join(' ')}
          />
          <Icon
            className={[
              'w-[18px] h-[18px] shrink-0 transition-colors duration-150',
              isActive ? 'text-primary' : 'text-text-dim group-hover:text-text-muted',
            ].join(' ')}
          />
          {!collapsed && <span className="flex-1 truncate">{label}</span>}
          {collapsed && <span className="rail-tip">{label}</span>}
        </>
      )}
    </NavLink>
  )
}

export default function Layout() {
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const { staff, logout } = useAuthStore()
  const { theme, toggleTheme, sidebarCollapsed, toggleSidebar } = useUIStore()

  useEffect(() => { applyTheme(theme) }, [theme])

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault()
        toggleSidebar()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggleSidebar])

  const handleLogout = () => {
    logout()
    toast.success('Signed out')
    navigate('/login', { replace: true })
  }

  const isDark = theme === 'dark'
  const initials = (staff?.full_name || staff?.name || staff?.username || staff?.email || '?')
    .split(/[\s@._]+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase()

  return (
    <div className="min-h-screen bg-surface text-text-primary">
      <aside
        className={[
          'glass fixed inset-y-0 left-0 z-40 flex flex-col border-r border-line',
          'transition-[width] duration-300 ease-snap',
          sidebarCollapsed ? 'w-[68px]' : 'w-60',
        ].join(' ')}
      >
        {/* Brand + collapse control */}
        <div
          className={[
            'flex items-center h-16 shrink-0 border-b border-line gap-2',
            sidebarCollapsed ? 'justify-center px-2' : 'pl-4 pr-2',
          ].join(' ')}
        >
          {sidebarCollapsed ? (
            <img src="/logo-mark.png" alt="Spotter"
                 className="w-9 h-9 rounded-lg bg-white object-contain shrink-0 shadow-soft" />
          ) : (
            <>
              {/* the real wordmark from the brand artwork, not typeset text */}
              <img src="/logo-lockup-dark.png" alt="Spotter"
                   className="h-7 w-auto object-contain dark:hidden animate-fade-in" />
              <img src="/logo-lockup-light.png" alt="Spotter"
                   className="h-7 w-auto object-contain hidden dark:block animate-fade-in" />
              <div className="flex-1" />
            </>
          )}

          {!sidebarCollapsed && (
            <button
              type="button"
              onClick={toggleSidebar}
              title="Collapse sidebar (Ctrl+B)"
              aria-label="Collapse sidebar"
              className="grid place-items-center w-8 h-8 shrink-0 rounded-lg border border-line-strong
                         text-text-muted hover:text-primary hover:border-primary/50 hover:bg-surface-3
                         transition-all duration-150 ease-snap active:scale-95"
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          )}
        </div>

        {sidebarCollapsed && (
          <div className="px-2 pt-2">
            <button
              type="button"
              onClick={toggleSidebar}
              title="Expand sidebar (Ctrl+B)"
              aria-label="Expand sidebar"
              className="group relative grid place-items-center w-full h-9 rounded-lg
                         border border-line-strong text-text-muted hover:text-primary
                         hover:border-primary/50 hover:bg-surface-3 transition-all duration-150
                         ease-snap active:scale-95"
            >
              <PanelLeftOpen className="w-4 h-4" />
              <span className="rail-tip">Expand (Ctrl+B)</span>
            </button>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-3 space-y-1">
          {NAV_ITEMS.map((item) => (
            <NavItem key={item.to} {...item} collapsed={sidebarCollapsed} />
          ))}
        </nav>

        <div className="shrink-0 border-t border-line p-2.5 space-y-1">
          <button
            type="button"
            onClick={toggleTheme}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className={[
              'group relative flex w-full items-center rounded-lg text-sm font-medium',
              'text-text-muted hover:text-text-primary hover:bg-surface-3',
              'transition-all duration-150 ease-snap',
              sidebarCollapsed ? 'justify-center py-2.5' : 'gap-3 px-3 py-2.5',
            ].join(' ')}
          >
            {isDark
              ? <Sun className="w-[18px] h-[18px] shrink-0 text-text-dim group-hover:text-primary transition-colors" />
              : <Moon className="w-[18px] h-[18px] shrink-0 text-text-dim group-hover:text-primary transition-colors" />}
            {!sidebarCollapsed && (
              <span className="flex-1 text-left">{isDark ? 'Light mode' : 'Dark mode'}</span>
            )}
            {sidebarCollapsed && <span className="rail-tip">{isDark ? 'Light mode' : 'Dark mode'}</span>}
          </button>

          <div
            className={[
              'flex items-center rounded-lg bg-surface-3/60 mt-1',
              sidebarCollapsed ? 'justify-center p-2' : 'gap-2.5 p-2',
            ].join(' ')}
          >
            <div className="grid place-items-center w-8 h-8 rounded-full bg-primary/15 text-primary text-xs font-bold shrink-0">
              {initials}
            </div>
            {!sidebarCollapsed && (
              <>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold truncate">
                    {staff?.full_name || staff?.name || staff?.username || 'Staff'}
                  </div>
                  <div className="text-[11px] text-text-dim truncate">{staff?.role || 'member'}</div>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  title="Sign out"
                  className="p-1.5 rounded-md text-text-dim hover:text-[rgb(var(--danger))] hover:bg-surface-4 transition-colors duration-150"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {sidebarCollapsed && (
            <button
              type="button"
              onClick={handleLogout}
              title="Sign out"
              className="group relative flex w-full justify-center py-2.5 rounded-lg text-text-dim hover:text-[rgb(var(--danger))] hover:bg-surface-3 transition-colors duration-150"
            >
              <LogOut className="w-[18px] h-[18px]" />
              <span className="rail-tip">Sign out</span>
            </button>
          )}
        </div>
      </aside>

      <main
        className={[
          'transition-[padding] duration-300 ease-snap',
          sidebarCollapsed ? 'pl-[68px]' : 'pl-60',
        ].join(' ')}
      >
        <div key={routerLocation.pathname} className="p-6 animate-rise">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
